import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Newspaper, Loader2, Send, Sparkles } from "lucide-react";
import { API, authFetch } from "@/App";
import DOMPurify from "dompurify";

export function NewsletterDialog({ 
  open, 
  onOpenChange, 
  newsletters = [],
  recipientCount = 0,
  initialSettings,
  onRefresh
}) {
  const [newsletterContent, setNewsletterContent] = useState({ subject: "", content: "" });
  const [generatingNewsletter, setGeneratingNewsletter] = useState(false);
  const [sendingNewsletter, setSendingNewsletter] = useState(false);
  const [newsletterSettings, setNewsletterSettings] = useState(
    initialSettings || { auto_send_enabled: false, send_day: "monday", send_time: "09:00", include_workshops: true }
  );

  const handleGenerateNewsletter = async () => {
    setGeneratingNewsletter(true);
    try {
      const response = await authFetch(`${API}/consultant/newsletter/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ include_workshops: newsletterSettings.include_workshops })
      });
      if (response.ok) {
        const data = await response.json();
        setNewsletterContent({
          subject: data.subject || "",
          content: data.content || ""
        });
        toast.success("Newsletter generated!");
      }
    } catch (_error) {
      toast.error("Failed to generate newsletter");
    } finally {
      setGeneratingNewsletter(false);
    }
  };

  const handleSendNewsletter = async () => {
    if (!newsletterContent.subject || !newsletterContent.content) {
      toast.error("Subject and content are required");
      return;
    }
    setSendingNewsletter(true);
    try {
      const response = await authFetch(`${API}/consultant/newsletter/send`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(newsletterContent)
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(data.message || "Newsletter sent!");
        setNewsletterContent({ subject: "", content: "" });
        onRefresh?.();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to send newsletter");
      }
    } catch (_error) {
      toast.error("Failed to send newsletter");
    } finally {
      setSendingNewsletter(false);
    }
  };

  const handleSaveNewsletterSettings = async () => {
    try {
      const response = await authFetch(`${API}/consultant/newsletter/settings`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(newsletterSettings)
      });
      if (response.ok) {
        toast.success("Newsletter settings saved!");
      }
    } catch (_error) {
      toast.error("Failed to save settings");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center">
            <Newspaper className="w-5 h-5 mr-2 text-blue-600" />
            Newsletter Management
          </DialogTitle>
          <DialogDescription>
            Generate AI newsletters and send to all community members
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="compose" className="flex-1 overflow-hidden flex flex-col">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="compose">Compose</TabsTrigger>
            <TabsTrigger value="history">History ({newsletters.length})</TabsTrigger>
            <TabsTrigger value="settings">Auto-Send</TabsTrigger>
          </TabsList>

          <TabsContent value="compose" className="flex-1 overflow-y-auto space-y-4 mt-4">
            <div className="flex items-center justify-between">
              <p className="text-sm text-neutral-500">
                Recipients: <strong>{recipientCount}</strong> members
              </p>
              <Button
                onClick={handleGenerateNewsletter}
                disabled={generatingNewsletter}
                className="bg-purple-600 hover:bg-purple-700"
                data-testid="generate-newsletter-btn"
              >
                {generatingNewsletter ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Generating...</>
                ) : (
                  <><Sparkles className="w-4 h-4 mr-2" />Generate with AI</>
                )}
              </Button>
            </div>

            <div className="space-y-2">
              <Label>Subject Line</Label>
              <Input
                value={newsletterContent.subject}
                onChange={(e) => setNewsletterContent({ ...newsletterContent, subject: e.target.value })}
                placeholder="Weekly Business Tips from My Biz Consulting"
                data-testid="newsletter-subject-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Newsletter Content (HTML)</Label>
              <Textarea
                value={newsletterContent.content}
                onChange={(e) => setNewsletterContent({ ...newsletterContent, content: e.target.value })}
                placeholder="Write or generate your newsletter content..."
                className="min-h-[200px] font-mono text-sm"
                data-testid="newsletter-content-input"
              />
            </div>

            {newsletterContent.content && (
              <div className="border rounded-lg p-4">
                <p className="text-xs text-neutral-500 mb-2">Preview:</p>
                <div 
                  className="prose prose-sm max-w-none"
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(newsletterContent.content) }}
                />
              </div>
            )}

            <Button
              onClick={handleSendNewsletter}
              disabled={sendingNewsletter || !newsletterContent.subject || !newsletterContent.content}
              className="w-full bg-blue-600 hover:bg-blue-700"
              data-testid="send-newsletter-btn"
            >
              {sendingNewsletter ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending...</>
              ) : (
                <><Send className="w-4 h-4 mr-2" />Send Newsletter Now</>
              )}
            </Button>
          </TabsContent>

          <TabsContent value="history" className="flex-1 overflow-y-auto mt-4">
            {newsletters.length === 0 ? (
              <div className="text-center py-8 text-neutral-500">
                <Newspaper className="w-12 h-12 mx-auto mb-2 text-neutral-300" />
                <p>No newsletters sent yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {newsletters.map((nl) => (
                  <div key={nl.newsletter_id} className="p-3 bg-neutral-50 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-medium text-neutral-900">{nl.subject}</h4>
                      <Badge variant="outline" className={nl.status === "sent" ? "border-green-300 text-green-700" : "border-amber-300 text-amber-700"}>
                        {nl.status}
                      </Badge>
                    </div>
                    <div className="flex items-center space-x-4 text-xs text-neutral-500">
                      <span>Sent: {new Date(nl.sent_at).toLocaleDateString()}</span>
                      <span>Recipients: {nl.success_count || nl.recipient_count}</span>
                      {nl.failed_count > 0 && (
                        <span className="text-red-500">Failed: {nl.failed_count}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="settings" className="flex-1 overflow-y-auto mt-4 space-y-4">
            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h4 className="font-medium text-blue-900">Automated Weekly Newsletter</h4>
                  <p className="text-sm text-blue-700">AI-generated newsletters sent automatically</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={newsletterSettings.auto_send_enabled}
                    onChange={(e) => setNewsletterSettings({ ...newsletterSettings, auto_send_enabled: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-neutral-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Send Day</Label>
                  <Select value={newsletterSettings.send_day} onValueChange={(v) => setNewsletterSettings({ ...newsletterSettings, send_day: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="monday">Monday</SelectItem>
                      <SelectItem value="tuesday">Tuesday</SelectItem>
                      <SelectItem value="wednesday">Wednesday</SelectItem>
                      <SelectItem value="thursday">Thursday</SelectItem>
                      <SelectItem value="friday">Friday</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Send Time</Label>
                  <Input
                    type="time"
                    value={newsletterSettings.send_time}
                    onChange={(e) => setNewsletterSettings({ ...newsletterSettings, send_time: e.target.value })}
                  />
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Include Upcoming Workshops</Label>
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={newsletterSettings.include_workshops}
                  onChange={(e) => setNewsletterSettings({ ...newsletterSettings, include_workshops: e.target.checked })}
                  className="w-4 h-4 rounded border-neutral-300"
                />
                <span className="text-sm text-neutral-600">Automatically include upcoming events in newsletters</span>
              </div>
            </div>

            <Button onClick={handleSaveNewsletterSettings} className="w-full">
              Save Settings
            </Button>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
